
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { CodeBlock, Preview } from "@lighting-beetle/lighter-styleguide";
import ToggleButton from "./utils/ToggleButton.js";
import Button from "../components/Button";
import Icon from "../components/Icon";

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Toggle`}</h1>
    <p>{`Toggle is a handy module used for toggling attribute values, class names, text content and icons on any element with a click.`}</p>
    <p>{`To start toggling, you need a trigger and a target (or multiple targets). Settings are passed into `}<inlineCode parentName="p">{`data-toggle`}</inlineCode>{` attribute of the trigger element as an array of objects. Each setting object holds information about how to find targets, which attribute should be toggled and a new value.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`[
  {
    target: "selector / self / previous / next / parent",
    attribute: "class / text / icon / element attribute",
    value: "desired value. omit for toggling true/false",
  },
];
`}</code></pre>
    <h2>{`Toggle attributes`}</h2>
    <h3>{`Class name`}</h3>
    <p>{`Specified class name is added/removed to/from the target element.`}</p>
    <p>{`The example toggles `}<inlineCode parentName="p">{`.text-bold`}</inlineCode>{` utility class on the example target`}</p>
    <Preview code={[<p id="class-toggle-example">example</p>, <Button data-toggle="[
      {
        target: '#class-toggle-example',
        attribute: 'class',
        value: 'text-bold',
      },
    ]" mdxType="Button">
      Toggle class
    </Button>]} mdxType="Preview">
  <p id="class-toggle-example">example</p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#class-toggle-example",
        attribute: "class",
        value: "text-bold"
      }])} mdxType="ToggleButton">
    Toggle class
  </ToggleButton>
    </Preview>
    <h3>{`Text`}</h3>
    <p>{`Text content of the target element is being switched between the original and that defined in value.`}</p>
    <Preview code={[<p id="text-toggle-target">Toggle off</p>, <Button data-toggle="[
        {
          target: '#text-toggle-target',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle off
    </Button>]} mdxType="Preview">
  <p id="text-toggle-target">Toggle off</p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#text-toggle-target",
        attribute: "text",
        value: "Toggle on"
      }])} mdxType="ToggleButton">
    Toggle text
  </ToggleButton>
    </Preview>
    <p>{`It might be necessary only to toggle a portion of the target element, especially if it contains other descendants (e.g. icons). This is solved by wrapping the text content inside a span with `}<inlineCode parentName="p">{`[data-toggle-text-target]`}</inlineCode>{` attribute.`}</p>
    <Preview code={[<p id="text-toggle-target-2">
      <Icon name="home" mdxType="Icon" />,<span data-toggle-text-target>Toggle off</span>
    </p>, <Button data-toggle="[
        {
          target: '#text-toggle-target-2',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle off
    </Button>]} mdxType="Preview">
  <p id="text-toggle-target-2">
    <Icon name="home" mdxType="Icon" /> <span data-toggle-text-target>Toggle off</span>
  </p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#text-toggle-target-2",
        attribute: "text",
        value: "Toggle on"
      }])} mdxType="ToggleButton">
    Toggle text
  </ToggleButton>
    </Preview>
    <h3>{`Icon`}</h3>
    <p>{`Switches `}<inlineCode parentName="p">{`xlink:href`}</inlineCode>{` attribute of the first found `}<inlineCode parentName="p">{`<use>`}</inlineCode>{` element inside the target element.`}</p>
    <Preview code={[<Icon id="icon-toggle-target" name="home" mdxType="Icon" />, <Button data-toggle="[
        {
          target: '#icon-toggle-target',
          attribute: 'icon',
          value: '/sprite.svg#heart',
        },
      ]" mdxType="Button">
      Toggle off
    </Button>]} mdxType="Preview">
  <p>
    <Icon id="icon-toggle-target" name="home" mdxType="Icon" />
  </p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#icon-toggle-target",
        attribute: "icon",
        value: "/sprite.svg#heart"
      }])} mdxType="ToggleButton">
    Toggle icon
  </ToggleButton>
    </Preview>
    <h3>{`Attribute value`}</h3>
    <p>{`Switches the specified attribute value.`}</p>
    <Preview code={[<p id="title-toggle-target" title="toggle off">
      Title attribute toggle target
    </p>, <Button data-toggle="[
        {
          target: '#title-toggle-target',
          attribute: 'title',
          value: 'toggle on',
        },
      ]" mdxType="Button">
      Toggle title attribute
    </Button>]} mdxType="Preview">
  <p id="title-toggle-target" title="toggle off">
    Title attribute toggle target
  </p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#title-toggle-target",
        attribute: "title",
        value: "toggle on"
      }])} mdxType="ToggleButton">
    Toggle title attribute
  </ToggleButton>
    </Preview>
    <h3>{`Boolean value attribute`}</h3>
    <p>{`Switches between boolean values on the specified value. Notice the omited value in data-toggle config.`}</p>
    <Preview code={[<p id="aria-expanded-toggle-target" aria-expanded="false">
      aria-expanded attribute toggle target
    </p>, <Button data-toggle="[
        {
          target: '#aria-expanded-toggle-target',
          attribute: 'aria-expanded',
        },
      ]" mdxType="Button">
      Toggle aria-expanded
    </Button>]} mdxType="Preview">
  <p id="aria-expanded-toggle-target" aria-expanded="false">
    aria-expanded attribute toggle target
  </p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#aria-expanded-toggle-target",
        attribute: "aria-expanded"
      }])} mdxType="ToggleButton">
    Toggle aria-expanded
  </ToggleButton>
    </Preview>
    <h3>{`Multiple attributes`}</h3>
    <p>{`Each settings object only controls one attribute. To toggle multiple attributes, simply use several settings objects for the same target`}</p>
    <Preview code={[<p id="multiple-attributes-toggle-target">
      <Icon name="home" mdxType="Icon" /> <span data-toggle-text-target>Toggle off</span>
    </p>, <Button data-toggle={[{
      target: "#multiple-attributes-toggle-target",
      attribute: "text",
      value: "Toggle on"
    }, {
      target: "#multiple-attributes-toggle-target",
      attribute: "icon",
      value: "/sprite.svg#heart"
    }, {
      target: "#multiple-attributes-toggle-target",
      attribute: "class",
      value: "text-bold"
    }]} mdxType="Button">
      Toggle text, icon and class
    </Button>]} mdxType="Preview">
  <p id="multiple-attributes-toggle-target">
    <Icon name="home" mdxType="Icon" /> <span data-toggle-text-target>Toggle off</span>
  </p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#multiple-attributes-toggle-target",
        attribute: "text",
        value: "Toggle on"
      }, {
        target: "#multiple-attributes-toggle-target",
        attribute: "icon",
        value: "/sprite.svg#heart"
      }, {
        target: "#multiple-attributes-toggle-target",
        attribute: "class",
        value: "text-bold"
      }])} mdxType="ToggleButton">
    Toggle text, icon and class
  </ToggleButton>
    </Preview>
    <h2>{`Toggle targets`}</h2>
    <h3>{`Selector`}</h3>
    <p>{`Passing a selector (or comma delimited list of selectors) searches for all matches in the DOM.`}</p>
    <Preview code={[<p data-selector-based-toggle-target>Toggle off</p>, <Button data-toggle="[
        {
          target: '[data-selector-based-toggle-target]',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle off
    </Button>]} mdxType="Preview">
  <p data-selector-based-toggle-target>Toggle off</p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "[data-selector-based-toggle-target]",
        attribute: "text",
        value: "Toggle on"
      }])} mdxType="ToggleButton">
    Toggle text
  </ToggleButton>
    </Preview>
    <h3>{`Self`}</h3>
    <p>{`Self targets the trigger element itself.`}</p>
    <Preview code={[<Button data-toggle="[
        {
          target: 'self',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle off
    </Button>]} mdxType="Preview">
  <ToggleButton data-toggle={JSON.stringify([{
        target: "self",
        attribute: "text",
        value: "Toggle on"
      }])} mdxType="ToggleButton">
    Toggle off
  </ToggleButton>
    </Preview>
    <h3>{`Previous`}</h3>
    <p>{`Previous targets the previous sibbling element.`}</p>
    <Preview code={[<p>Toggle off</p>, <Button data-toggle="[
        {
          target: 'previous',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle previous element
    </Button>]} mdxType="Preview">
  <p>Toggle off</p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "previous",
        attribute: "text",
        value: "Toggle on"
      }])} mdxType="ToggleButton">
    Toggle previous element
  </ToggleButton>
    </Preview>
    <h3>{`Next`}</h3>
    <p>{`Next targets the next sibbling element.`}</p>
    <Preview code={[<Button data-toggle="[
        {
          target: 'next',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle next element
    </Button>, <p>Toggle off</p>]} mdxType="Preview">
  <ToggleButton data-toggle={JSON.stringify([{
        target: "next",
        attribute: "text",
        value: "Toggle on"
      }])} mdxType="ToggleButton">
    Toggle next element
  </ToggleButton>
  <p>Toggle off</p>
    </Preview>
    <h3>{`Parent`}</h3>
    <p>{`Parent targets the target's parent element.`}</p>
    <p>{`The example toggles `}<inlineCode parentName="p">{`.text-right`}</inlineCode>{` utility class on the parent element.`}</p>
    <Preview code={[<Button data-toggle="[
        {
          target: 'parent',
          attribute: 'text',
          value: 'Toggle on',
        },
      ]" mdxType="Button">
      Toggle parent
    </Button>, <p>Toggle off</p>]} mdxType="Preview">
  <div>
    <ToggleButton data-toggle={JSON.stringify([{
          target: "parent",
          attribute: "class",
          value: "text-right"
        }])} mdxType="ToggleButton">
      Toggle parent
    </ToggleButton>
  </div>
    </Preview>
    <h3>{`Multiple toggle targets`}</h3>
    <p>{`Since data-toggle is an array of objects, it accepts multiple settings objects, which can specify multiple targets.`}</p>
    <p>{`The target property can query for multiple comma delimited declarations.`}</p>
    <p>{`To get Toggle module works properly, it is important to keep right order of specific targets, because targets are triggered one by one.`}</p>
    <Preview code={[<p>
      <Icon id="multiple-toggle-target-1" name="home" mdxType="Icon" />
      <Icon id="multiple-toggle-target-2" name="home" mdxType="Icon" />
      <Icon id="multiple-toggle-target-3" name="home" mdxType="Icon" />
      <Icon className="multiple-toggle-target" name="home" mdxType="Icon" />
      <Icon className="multiple-toggle-target" name="home" mdxType="Icon" />
      <Icon className="multiple-toggle-target" name="home" mdxType="Icon" />
    </p>, <Button data-toggle={[{
      target: "#multiple-toggle-target-1, #multiple-toggle-target-2, #multiple-toggle-target-3",
      attribute: "icon",
      value: "/sprite.svg#heart"
    }, {
      target: ".multiple-toggle-target",
      attribute: "icon",
      value: "/sprite.svg#heart"
    }]} mdxType="Button">
      Toggle all targets
    </Button>]} mdxType="Preview">
  <p>
    <Icon id="multiple-toggle-target-1" name="home" mdxType="Icon" />
    <Icon id="multiple-toggle-target-2" name="home" mdxType="Icon" />
    <Icon id="multiple-toggle-target-3" name="home" mdxType="Icon" />
    <Icon className="multiple-toggle-target" name="home" mdxType="Icon" />
    <Icon className="multiple-toggle-target" name="home" mdxType="Icon" />
    <Icon className="multiple-toggle-target" name="home" mdxType="Icon" />
  </p>
  <ToggleButton data-toggle={JSON.stringify([{
        target: "#multiple-toggle-target-1, #multiple-toggle-target-2, #multiple-toggle-target-3",
        attribute: "icon",
        value: "/sprite.svg#heart"
      }, {
        target: ".multiple-toggle-target",
        attribute: "icon",
        value: "/sprite.svg#heart"
      }])} mdxType="ToggleButton">
    Toggle all targets
  </ToggleButton>
    </Preview>
`;
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;